import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReportTableComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { FileHelperService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { ReportFilter } from '@reporting/interfaces/report-filter.interface';
import { STANDARD_REPORTS } from '@reporting/meta/standard-reports.meta';
import { ReportingService } from '@reporting/services/reporting.service';
import * as Sentry from '@sentry/browser';
import { format, isBefore, parse, subYears } from 'date-fns';
import { saveAs } from 'file-saver';
import { StandardReports } from '../../interfaces/standard-reports.interface';
import { Report } from '../../services/queries/report';

@Component({
    templateUrl: './standard-report.view.html',
    styleUrls: ['./standard-report.style.scss'],
})
export class StandardReportView implements OnInit, AfterViewInit {
    @ViewChild('reportTable') reportTable!: ReportTableComponent;

    /**
     * Loading Indicator
     */
    isLoading = false;
    missingCount = 0;
    reportingServiceData: any;
    filters = {};
    filterValues = {};
    activeFilters: any = {
        DateRange: null,
        Filters: {},
    };
    initialDateRange: string[] = [];
    report: StandardReports = { name: '', description: '', routerLink: '' };
    theReport: any;
    initialStartAt: Date = subYears(new Date(), 1);
    initialEndAt: Date = new Date();
    chartData: any = {};
    detailAdditionsAndTerminationsReport!: Report;
    shouldLoadDetailReport = false;
    hasDetailAdditionsAndTerminationsReport = false;
    showChart = true;
    showChartData = false;

    constructor(
        private reportingService: ReportingService,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private fileService: FileHelperService,
        private featureService: FeatureService
    ) {
        const reportSlug = this.route.snapshot.params['report'];
        this.report = STANDARD_REPORTS.find((report) => report.routerLink === reportSlug) ?? this.report;
    }

    get name(): string {
        return this.report.name;
    }

    get description(): string {
        return this.report.description;
    }

    /**
     * Lifecycle - On Init
     */
    async ngOnInit(): Promise<void> {
        this.theReport = this.report.legacySlug;
        this.initialDateRange = [format(this.initialStartAt), format(this.initialEndAt)];
        this.loadReport({ DateRange: this.initialDateRange });

        this.shouldLoadDetailReport = !!this.report.detailData;
        this.activeFilters.DateRange = this.initialDateRange;
        this.activeFilters.Filters.DateRange = this.initialDateRange;

        this.hasDetailAdditionsAndTerminationsReport = await this.featureService.has(
            FeatureFlag.detailAdditionsAndTerminationsReport
        );
    }

    ngAfterViewInit(): void {
        this.loadDetailReport({ DateRange: this.initialDateRange });
    }

    onFilter(filters: any): void {
        this.activeFilters.Filters = filters;
        // incoming filters here do not include the dateRange so we must add it back
        this.loadReport(Object.assign(this.activeFilters.Filters, { DateRange: this.activeFilters.DateRange }));
        this.onFilterDetailReport(
            Object.assign(this.activeFilters.Filters, { DateRange: this.activeFilters.DateRange })
        );
    }

    onDateFilter(filters: any): void {
        this.activeFilters.DateRange = filters;
        const isValid = this.isValidDateRange(this.activeFilters.DateRange[0], this.activeFilters.DateRange[1]);
        const hasNeitherDate = !this.activeFilters.DateRange[0] && !this.activeFilters.DateRange[1];
        if (isValid) {
            this.loadReport(Object.assign(this.activeFilters.Filters, { DateRange: this.activeFilters.DateRange }));
            this.onFilterDetailReport(
                Object.assign(this.activeFilters.Filters, { DateRange: this.activeFilters.DateRange })
            );
        }
        if (hasNeitherDate) {
            this.loadReport(Object.assign(this.activeFilters.Filters, { DateRange: null }));
            this.onFilterDetailReport(Object.assign(this.activeFilters.Filters, { DateRange: null }));
        }
    }

    onExport(fileSaver = saveAs): void {
        this.isLoading = true;
        const endPoint = `export-${this.theReport}`;
        this.reportingService
            .getStandardReportExport(endPoint, this.activeFilters.Filters)
            .subscribe((reportExport) => {
                const filename = `${this.theReport}_report`;
                fileSaver(reportExport, filename);
                this.isLoading = false;
            });
    }
    public showChartView(): void {
        this.showChart = true;
        this.showChartData = false;

        return;
    }

    public showChartDataView(): void {
        this.showChartData = true;
        this.showChart = false;

        return;
    }

    private isValidDateRange(startAt: any, endAt: any): boolean {
        if (!startAt || !endAt) {
            return false;
        }

        return isBefore(parse(startAt), parse(endAt));
    }

    private parseFilters(report: any): void {
        for (const filter of report.filters) {
            this.filters[filter.name] = filter.name;
            if (filter.options_ids) {
                this.filterValues[filter.name] = {};
                for (const key in filter.options_ids) {
                    this.filterValues[filter.name][filter.options_ids[key]] = filter.options[key];
                }
            } else if (filter.options) {
                this.filterValues[filter.name] = {};
                for (const option of filter.options) {
                    this.filterValues[filter.name][option] = option;
                }
            }
        }
    }

    private loadReport(filters?: any): void {
        this.isLoading = true;
        this.reportingService
            .getStandardReport(this.theReport, filters)
            .then((report) => {
                this.missingCount = report.reportingService.missing;
                this.reportingServiceData = report.reportingService;
                const initialObject = report;
                this.parseFilters(report);
                this.chartData = initialObject.reportingService;
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                Sentry.captureException(err);
                this.notify.error('Could not load report.');
            });
    }

    private async loadDetailReport(filters?: any): Promise<void> {
        if (!this.shouldLoadDetailReport) {
            return;
        }

        const formattedFilters = this.queryParamConverterForNewReport(filters);
        this.isLoading = true;

        try {
            this.detailAdditionsAndTerminationsReport = await new Report(this.fileService)
                .name('detailAdditionsAndTerminations')
                .filters(formattedFilters)
                .page(1)
                .get();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private onFilterDetailReport(filters?: any): void {
        /*
         * The `queryParamConverterForNewReport` method processes each filter by:
         * Iterating over each filter key and converting the value into the new standard report filtering format.
         * If a filter value is an array of length 1, it simplifies it to a single value for
         * the report query.
         * */
        const formattedFilters = this.queryParamConverterForNewReport(filters);

        this.detailAdditionsAndTerminationsReport
            .filters(formattedFilters)
            .page(1)
            .get()
            .then((report: Report) => {
                this.detailAdditionsAndTerminationsReport = report;
                this.reportTable.setPaginationData();
            })
            .catch((err) => this.notify.error(err))
            .finally(() => (this.isLoading = false));
    }
    private queryParamConverterForNewReport(filters?: any): ReportFilter[] {
        if (!filters.DateRange) {
            return [];
        }

        return Object.keys(filters).map((key) => {
            const value = filters[key];

            // If the array has only one value, we return just the value (string or number)
            const reportFilterValue = value.length === 1 ? value[0] : value;

            return {
                name: key,
                value: reportFilterValue,
            };
        });
    }
}
